import React, { useMemo, useState } from 'react';

import Descriptor from '@r6digital/rapidstor-types/react/entities/Descriptor';
import UnitType, {
    distributeUnitsToGroups,
    UnitGroup,
} from '@r6digital/rapidstor-types/react/entities/UnitType';

import classes from './Descriptor.module.scss';

import { pickUnitType } from '../../features/helpers';
import UnitTypeElement from './UnitType';

interface Props extends React.HTMLAttributes<HTMLElement> {
    descriptor: Descriptor;
    recommended: boolean;
    reservationIsLoading: boolean;
    onBookUnitClicked: () => void;
    onEmailQuoteClicked: () => void;
    onUnitTypeSelected: (descriptor: Descriptor, unitType: UnitType) => void;
    locationSlug: string;
}

function DescriptorElement({
    onBookUnitClicked,
    onEmailQuoteClicked,
    descriptor,
    recommended,
    reservationIsLoading,
}: Props) {
    const unitGroups = useMemo(
        () => distributeUnitsToGroups(descriptor.unitTypes),
        [descriptor.unitTypes]
    );

    // Remove groups that don't have a unit type assigned to them
    const validGroups = useMemo(
        () =>
            Object.entries(unitGroups)
                .filter(([, type]) => type)
                .map(([group]) => group as UnitGroup)
                .sort(),
        [unitGroups]
    );

    const bestAvailableGroup = useMemo(
        () =>
            Object.entries(unitGroups)
                .filter(([, type]) => type)
                .sort(([, a], [, b]) => {
                    if (a === null || b === null) {
                        return -1;
                    }
                    if (!!a.totalAvailable === !!b.totalAvailable) {
                        return a.stdRate - b.stdRate;
                    }
                    return a.totalAvailable ? -1 : 1;
                })
                .map(([group]) => group as UnitGroup)[0],
        [unitGroups]
    );

    const [group] = useState<UnitGroup>(bestAvailableGroup ?? validGroups[0]);
    // The unitType variable will update when the group variable changes.
    // const unitType = useMemo(() => unitGroups[group] as UnitType, [group, unitGroups]);
    const unitType = pickUnitType(descriptor);

    return (
        <div className={classes.descriptor}>
            <div className={classes.card}>
                <div className={classes.card_body}>
                    {unitType.deal && (
                        <div className={`${classes.deal_label}`}>
                            <span>{unitType.deal.title}</span>
                        </div>
                    )}
                    <div className={'row'} style={{ padding: '14px 32px 28px 32px' }}>
                        <div>
                            {unitGroups[group] && (
                                <UnitTypeElement
                                    onBookUnitClicked={onBookUnitClicked}
                                    onEmailQuoteClicked={onEmailQuoteClicked}
                                    descriptor={descriptor}
                                    unitType={unitType}
                                    reservationIsLoading={reservationIsLoading}
                                    recommended={recommended}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default DescriptorElement;
